import * as React from "react"
import { useEffect, useMemo, useState } from "react"
import { useLanguage } from "@/lib/lang"
import { t, tf, direction, formatTemplate } from "@/lib/i18n"
import type { Language } from "@/lib/i18n"
import { useLocation } from "react-router-dom"
import { resolveLocationNames } from "@/lib/name-resolver"
import { resolveOfficialPersonName, isSelectionReady } from "@/lib/names"
import { getPanchayatDataProvider, type PanchayatInstitutionRecord } from "@/lib/panchayat-data"
import { V2GP, BsData, ZpData, loadBsData, loadZpData, V2Names, BsZpPerson } from "@/lib/xlsx-loader"
import { buildWhatsAppImageRequestMessage, resolveWhatsAppName, sanitizeForWhatsApp } from "@/lib/whatsapp-utils"
import { WardList } from "@/components/gram-panchayat/WardList"
import { LeaderProfileCard } from "@/components/gram-panchayat/LeaderProfileCard"
import { DynamicImage } from "@/components/ui/DynamicImage"
import { useLocationNames } from "@/hooks/useLocationNames"
import { extractItemId, generateId } from "@/lib/engagementId"
import { useAuth } from "@/contexts/AuthContext";
import { getDignitaryEntityId } from "@/lib/profileEntityId";

function formatBsZpLoadError(err: unknown, lang: Language): string {
  const failed = t("errors.failed_to_load", lang) || "Failed to load data."
  const notFound = t("errors.not_found", lang) || failed
  const timeout = t("errors.timeout", lang) || failed
  const network = t("errors.network", lang) || failed
  const e = err as any
  const code = String(e?.code || "")
  if (code === "FILE_NOT_FOUND") return notFound
  if (code === "TIMEOUT") return timeout
  if (code === "BAD_RESPONSE") return network
  if (code === "BAD_FILE" || code === "PARSE_FAILED") return failed
  return e instanceof Error ? e.message : failed
}

function useQuery() {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

function institutionToGpRecord(institution: PanchayatInstitutionRecord): V2GP {
  return {
    id: institution.id,
    names: institution.names,
    sarpanch: institution.sarpanch,
    wards: institution.wards,
  }
}

export function isBsDataMeaningful(item: BsData | null | undefined): item is BsData {
  if (!item) return false
  const chair = item.chairperson?.names
  const hasChair = Boolean(chair?.en || chair?.hi || chair?.pa)
  const hasWards = Array.isArray(item.wards) && item.wards.length > 0
  return hasChair || hasWards
}

export function isZpDataMeaningful(item: ZpData | null | undefined): item is ZpData {
  if (!item) return false
  const chair = item.chairperson?.names
  const hasChair = Boolean(chair?.en || chair?.hi || chair?.pa)
  const hasWards = Array.isArray(item.wards) && item.wards.length > 0
  return hasChair || hasWards
}

export function selectBsDataRecord(
  data: Record<string, BsData> | null | undefined,
  district: string,
  block: string,
  recordType: "current" | "previous"
): BsData | null {
  if (!data) return null
  const d = String(district || "").trim().toLowerCase()
  const b = String(block || "").trim().toLowerCase()
  if (!d || !b) return null

  const baseKey = `${d}|${b}`
  const candidates =
    recordType === "current"
      ? [baseKey]
      : [`${baseKey}|previous`, `${baseKey}:previous`, `${baseKey}|prev`, `${baseKey}:prev`]

  for (const k of candidates) {
    const item = (data as any)[k] as BsData | undefined
    if (isBsDataMeaningful(item)) return item
  }
  return null
}

export function selectZpDataRecord(
  data: Record<string, ZpData> | null | undefined,
  district: string,
  recordType: "current" | "previous"
): ZpData | null {
  if (!data) return null
  const d = String(district || "").trim().toLowerCase()
  if (!d) return null

  const baseKey = d
  const candidates =
    recordType === "current"
      ? [baseKey]
      : [`${baseKey}|previous`, `${baseKey}:previous`, `${baseKey}|prev`, `${baseKey}:prev`]

  for (const k of candidates) {
    const item = (data as any)[k] as ZpData | undefined
    if (isZpDataMeaningful(item)) return item
  }
  return null
}

const GramPanchayatPage: React.FC = () => {
  const { lang } = useLanguage()
  const { currentUser: user } = useAuth()
  console.log("AUTH USER:", user)
  const query = useQuery()
  const [district, setDistrict] = useState("")
  const [block, setBlock] = useState("")
  const [village, setVillage] = useState("")
  const [level, setLevel] = useState<"gp" | "bs" | "zp">("gp")
  const [personName, setPersonName] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [gpData, setGpData] = useState<V2GP | null>(null)
  const [bsData, setBsData] = useState<BsData | null>(null)
  const [zpData, setZpData] = useState<ZpData | null>(null)

  // Resolve localized names for display using the shared logic (same as WelcomeMessage)
  const { districtName, blockName, villageName, resolvedLang } = useLocationNames(district, block, village, lang)
  const displayLang = resolvedLang || "en"
  const gpVillageLookup = useMemo(() => {
    if (level !== "gp") return village
    return villageName || village
  }, [level, village, villageName])
  const [enKey, setEnKey] = useState<{ district: string; block: string; village: string }>({ district: "", block: "", village: "" })

  const [recordType, setRecordType] = useState<"current" | "previous">("current")
  const [switchLoading, setSwitchLoading] = useState(false)
  const [switchError, setSwitchError] = useState<string | null>(null)

  const cmItem = { id: "cm-main", name: t("cm_name", lang), role: "cm" }
  console.log("FULL ITEM:", cmItem)
  const cmItemId = String(extractItemId(cmItem) || generateId(cmItem))
  console.log("FINAL ITEM ID:", cmItemId)

  const ministerItem = { id: "minister-main", name: t("pm_name", lang), role: "minister" }
  console.log("FULL ITEM:", ministerItem)
  const ministerItemId = String(extractItemId(ministerItem) || generateId(ministerItem))
  console.log("FINAL ITEM ID:", ministerItemId)

  // Fetch Data (GP/BS/ZP) based on level and recordType
  useEffect(() => {
    let mounted = true
    const d = (district || "").trim()
    const b = (block || "").trim()
    const v = (village || "").trim()

    const safeSet = (fn: () => void) => {
      if (!mounted) return
      fn()
    }

    const run = async () => {
      if (level !== "gp") safeSet(() => setGpData(null))
      if (level !== "bs") safeSet(() => setBsData(null))
      if (level !== "zp") safeSet(() => setZpData(null))

      if (level === "gp") {
        if (!(d && b && v)) {
          safeSet(() => {
            setGpData(null)
            setEnKey({ district: "", block: "", village: "" })
          })
          return
        }

        console.info("[GramPanchayat] Loading shard for:", { d, b, v, recordType })
        safeSet(() => {
          setSwitchError(null)
          setSwitchLoading(true)
        })

        try {
          const enNames = await resolveLocationNames(d, b, v, "en")
          if (!mounted) return

          const dFetch = (enNames.districtName || d).trim()
          const bFetch = (enNames.blockName || b).trim()
          const vFetch = (enNames.villageName || v).trim()
          safeSet(() => setEnKey({ district: dFetch, block: bFetch, village: vFetch }))

          const provider = getPanchayatDataProvider()
          const institution = await provider.getInstitution(vFetch, dFetch, bFetch, recordType)
          if (!mounted) return

          if (!institution) {
            console.error("[GramPanchayat] Institution not found", { district: dFetch, block: bFetch, village: vFetch, recordType })
            return
          }

          safeSet(() => {
            setGpData(institutionToGpRecord(institution))
            setNameError(null)
          })
        } catch (error) {
          console.error("[GramPanchayat] Failed to load GP data", error)
          safeSet(() => setSwitchError(error instanceof Error ? error.message : String(error)))
          return
        } finally {
          safeSet(() => setSwitchLoading(false))
        }

        return
      }

      if (level === "bs") {
        if (!(d && b)) {
          safeSet(() => setBsData(null))
          return
        }

        safeSet(() => {
          setSwitchError(null)
          setSwitchLoading(true)
          setBsData(null)
        })

        try {
          const data = await loadBsData()
          if (!mounted) return
          const item = selectBsDataRecord(data, d, b, recordType)
          if (!item) {
            console.error("[GramPanchayat] BS data unavailable", { district: d, block: b, recordType })
            safeSet(() => {
              setBsData(null)
              setSwitchError(`Block Samiti data not found: ${d} / ${b}`)
            })
            return
          }
          safeSet(() => {
            setBsData(item)
            setNameError(null)
          })
        } catch (error) {
          console.error("[GramPanchayat] Failed to load BS data", error)
          safeSet(() => {
            setBsData(null)
            setSwitchError(formatBsZpLoadError(error, lang))
          })
          return
        } finally {
          safeSet(() => setSwitchLoading(false))
        }

        return
      }

      if (level === "zp") {
        if (!d) {
          safeSet(() => setZpData(null))
          return
        }

        safeSet(() => {
          setSwitchError(null)
          setSwitchLoading(true)
          setZpData(null)
        })

        try {
          const data = await loadZpData()
          if (!mounted) return
          const item = selectZpDataRecord(data, d, recordType)
          if (!item) {
            console.error("[GramPanchayat] ZP data unavailable", { district: d, recordType })
            safeSet(() => {
              setZpData(null)
              setSwitchError(`Zila Parishad data not found: ${d}`)
            })
            return
          }
          safeSet(() => {
            setZpData(item)
            setNameError(null)
          })
        } catch (error) {
          console.error("[GramPanchayat] Failed to load ZP data", error)
          safeSet(() => {
            setZpData(null)
            setSwitchError(formatBsZpLoadError(error, lang))
          })
          return
        } finally {
          safeSet(() => setSwitchLoading(false))
        }
      }
    }

    run().catch((error) => {
      console.error("[GramPanchayat] Unexpected load failure", error)
    })

    return () => {
      mounted = false
    }
  }, [level, district, block, village, recordType])

  // Validate template prerequisites per level
  const validateMessageFields = (lvl: "gp" | "bs" | "zp") => {
    const errs: string[] = []
    const rawName = resolveWhatsAppName(
      lvl,
      { gpData, bsData, zpData, fallbackName: personName },
      lang
    )
    const nameVal = sanitizeForWhatsApp(rawName, lang)
    const missingName = sanitizeForWhatsApp(t("whatsapp_name_missing", lang), lang)
    if (!nameVal || nameVal === missingName) errs.push("name")

    const designation = getImageCaption(lvl)
    if (!designation || designation.trim().length === 0) errs.push("designation")
    const distOk = Boolean((district || "").trim())
    const blockOk = Boolean((block || "").trim())
    if (lvl === "gp" && (!blockOk || !distOk)) errs.push("location")
    if (lvl === "bs" && (!blockOk || !distOk)) errs.push("location")
    if (lvl === "zp" && !distOk) errs.push("location")
    // Sender phone is not required for wa.me link
    return { errors: errs, phone: "" }
  }

  const getImageCaption = (lvl: "gp" | "bs" | "zp"): string => {
    // Localized captions based on level, with safe fallbacks
    const cap = lvl === "gp" ? t("sarpanch_title", lang) : t("chairperson_title", lang)
    if (!cap || cap.trim().length === 0) {
      return lvl === "gp" ? "Sarpanch" : "Chairperson"
    }
    return cap
  }

  const getImageCaptionRole = (lvl: "gp" | "bs" | "zp", role: "chair" | "vice"): string => {
    if (lvl === "gp") return getImageCaption(lvl)
    const key = role === "vice" ? "vice_chairperson_title" : "chairperson_title"
    const cap = t(key, lang)
    if (!cap || cap.trim().length === 0) {
      return role === "vice" ? "Vice-Chairperson" : "Chairperson"
    }
    return cap
  }

  const getPreviousBodyLabel = (lvl: "gp" | "bs" | "zp") => {
    const key = lvl === "gp" ? "previous_gram_panchayat" : lvl === "bs" ? "previous_block_samiti" : "previous_zila_parishad"
    const v = t(key, lang)
    if (v && v !== key) return v
    return formatTemplate(t("prev_body_label", lang), {
      level: lvl === "gp" ? t("level_gp", lang) : lvl === "bs" ? t("level_bs", lang) : t("level_zp", lang)
    })
  }

  const buildWhatsAppMessage = (lvl: "gp" | "bs" | "zp", name: string, designationOverride?: string): string => {
    const designationBase = designationOverride || getImageCaption(lvl)
    const gpVal = lvl === "gp"
      ? (gpData?.names?.[lang as "en" | "hi" | "pa"] || (lang === "en" ? (villageName || village) : (villageName || "")))
      : (lang === "en" ? (villageName || village) : (villageName || ""))
    const blockVal = lang === "en" ? (blockName || block) : (blockName || "")
    const districtVal = lang === "en" ? (districtName || district) : (districtName || "")

    return buildWhatsAppImageRequestMessage({
      lang,
      name,
      designation: designationBase,
      gp: gpVal,
      block: blockVal,
      district: districtVal,
      isFormerTenure: recordType === "previous"
    })
  }

  const buildWhatsAppHref = (lvl: "gp" | "bs" | "zp", name: string): string => {
    const msg = buildWhatsAppMessage(lvl, name)
    const encoded = encodeURIComponent(msg)
    // Use universal wa.me link which works on mobile and desktop
    return `https://wa.me/919466333211?text=${encoded}`
  }

  const handleWhatsAppClick: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = (e) => {
    e.preventDefault()
    try {
      const { errors } = validateMessageFields(level)
      if (errors.length > 0) {
        alert(
          `Please complete the following before sending: ${errors
            .map((x) => x)
            .join(", ")}`
        )
        return
      }

      const rawName = resolveWhatsAppName(
        level,
        { gpData, bsData, zpData, fallbackName: personName },
        lang
      )
      const name = sanitizeForWhatsApp(rawName, lang)

      // Use wa.me link directly as requested for stability and direct opening
      const webUrl = buildWhatsAppHref(level, name)

      // Open immediately in new tab/window - this maintains current page state
      window.open(webUrl, '_blank', 'noopener,noreferrer')
    } catch (err) {
      console.error("Error opening WhatsApp:", err)
      // Fallback
      const webUrl = buildWhatsAppHref(level, sanitizeForWhatsApp(personName || "", lang))
      window.open(webUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const handleWhatsAppClickWithName: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = (e) => {
    e.preventDefault()
    try {
      const { errors } = validateMessageFields(level)
      const filtered = errors.filter((x) => x !== "name")
      if (filtered.length > 0) {
        alert(
          `Please complete the following before sending: ${filtered
            .map((x) => x)
            .join(", ")}`
        )
        return
      }

      const targetName = e.currentTarget.dataset.waName || ""
      const targetRole = e.currentTarget.dataset.waRole === "vice" ? "vice" : "chair"
      const safeName = sanitizeForWhatsApp(targetName || t("whatsapp_name_missing", lang), lang)
      const designation = getImageCaptionRole(level, targetRole)
      const msg = buildWhatsAppMessage(level, safeName, designation)
      const encoded = encodeURIComponent(msg)
      const webUrl = `https://wa.me/919466333211?text=${encoded}`
      window.open(webUrl, "_blank", "noopener,noreferrer")
    } catch (err) {
      console.error("Error opening WhatsApp:", err)
    }
  }


  // Listen to selection changes from the header
  useEffect(() => {
    const onSel = (ev: Event) => {
      try {
        const det = (ev as CustomEvent).detail as { level?: "gp" | "bs" | "zp"; district?: string; block?: string; village?: string } | undefined
        if (!det) return
        if (det.level) setLevel(det.level)
        if (typeof det.district === "string") setDistrict(det.district)
        if (typeof det.block === "string") setBlock(det.block)
        if (typeof det.village === "string") setVillage(det.village)
      } catch {
        // Ignore invalid event detail
      }
    }
    window.addEventListener("app:selectionchange", onSel)
    return () => window.removeEventListener("app:selectionchange", onSel)
  }, [])

  useEffect(() => {
    const qDistrict = query.get("district") || ""
    const qBlock = query.get("block") || ""
    const qVillage = query.get("village") || ""
    const qLevel = (query.get("level") as "gp" | "bs" | "zp" | null) || null
    // Fallback to session storage if query is missing
    setDistrict(qDistrict || sessionStorage.getItem("sel.district") || "")
    setBlock(qBlock || sessionStorage.getItem("sel.block") || "")
    setVillage(qVillage || sessionStorage.getItem("sel.village") || "")
    const savedLevel = (sessionStorage.getItem("sel.level") as "gp" | "bs" | "zp" | null) || null
    setLevel(qLevel || savedLevel || "gp")
  }, [query])

  // Scroll to tabs if requested
  useEffect(() => {
    const scrollTo = query.get("scrollTo")
    if (scrollTo === "quick-links-strip") {
      // Small delay to ensure rendering
      setTimeout(() => {
        const element = document.getElementById("quick-links-strip")
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }, 500)
    }
  }, [query])

  // Resolve official person name whenever selection changes; independent from language
  useEffect(() => {
    const selReady = isSelectionReady(level, { district, block, village })
    if (!selReady) {
      setPersonName(null)
      setNameError(null)
      return
    }
    let cancelled = false
    resolveOfficialPersonName(level, { district, block, village })
      .then((name) => {
        if (cancelled) return
        setPersonName(name)
        setNameError(null)
      })
      .catch((err) => {
        if (cancelled) return
        setPersonName(null)
        // Provide specific error only for GP where we expect sarpanch data
        if (level === "gp") {
          const msg = (err && err.message) || "Failed to load sarpanch data"
          setNameError(msg)
        } else {
          setNameError(null)
        }
        console.error("Error resolving official person name:", err)
      })
    return () => {
      cancelled = true
    }
  }, [level, district, block, village])

  useEffect(() => {
    // Compute a single welcome title based on level and available selections
    let titleLocalized = ""
    if (level === "zp" && districtName) {
      titleLocalized = tf("welcome_zp_heading", displayLang, { district: districtName })
    } else if (level === "bs" && districtName && blockName) {
      titleLocalized = tf("welcome_bs_heading", displayLang, { block: blockName })
    } else if (level === "gp" && villageName) {
      titleLocalized = tf("welcome_gp_heading", displayLang, {
        village: villageName,
        block: blockName || "",
        district: districtName || ""
      })
    }
    if (titleLocalized) document.title = titleLocalized
    const metaDesc = `${level.toUpperCase()} page${districtName ? `, ${districtName}` : ""}${blockName ? `, ${blockName}` : ""}`
    const meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
    if (meta) meta.content = metaDesc
  }, [villageName, districtName, blockName, level, lang, displayLang])

  const showPrevBodyUnavailable =
    recordType === "previous" &&
    !switchLoading &&
    ((level === "bs" && Boolean(district && block) && !bsData) || (level === "zp" && Boolean(district) && !zpData))

  return (
    <main className="bg-white" dir={direction(displayLang)}>
      <section className="content-rail section">
        {/* Single welcome line only for the selected option */}
        {level === "zp" && district ? (
          <h1 className="heading-primary text-center text-accent">
            {tf("welcome_zp_heading", displayLang, { district: districtName || district })}
          </h1>
        ) : level === "bs" && district && block ? (
          <h1 className="heading-primary text-center text-accent">
            {tf("welcome_bs_heading", displayLang, { block: blockName || block })}
          </h1>
        ) : level === "gp" && village ? (
          <h1 className="heading-primary text-center text-accent">
            {tf("welcome_gp_heading", displayLang, { village: villageName || village, block: blockName || block, district: districtName || district })}
          </h1>
        ) : (
          <p className="text-center text-sm text-destructive">
            {level === "gp"
              ? !district
                ? t("select_district", lang)
                : !block
                  ? t("select_block", lang)
                  : t("select_village", lang)
              : level === "bs"
                ? !district
                  ? t("select_district", lang)
                  : t("select_block", lang)
                : t("select_district", lang)}
          </p>
        )}

        {/* District and Block on a single small line under welcome, without special styling */}
        {(((level === "zp" && district) || (level === "bs" && district && block) || (level === "gp" && village)) && (district || block)) && (
          <p className="text-center text-body mt-2">
            {[districtName || null, blockName || null]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}

        {/* Leaders Row: CM, Minister, Chairperson/Sarpanch (+ Vice-Chairperson for BS/ZP) */}
        <div className={`mt-8 grid grid-cols-1 ${level === "gp" ? "md:grid-cols-3 max-w-5xl" : "md:grid-cols-4 max-w-6xl"} gap-6 justify-items-center items-start mx-auto`}>
          {/* 1. Chief Minister */}
          <LeaderProfileCard
            id="cm-main"
            engagementKey={cmItemId}
            level={level}
            tenure="current"
            position="chief-minister"
            entityIdOverride={getDignitaryEntityId("cm_nayab_saini")}
            district={enKey.district || district}
            block={enKey.block || block}
            village={enKey.village || village}
            imageSrc="/images/cm.jpg"
            alt={t("caption_cm", lang)}
            altKey="caption_cm"
            name={t("cm_name", lang)}
            designation={t("cm_designation", lang)}
          />

          {/* 2. Panchayat Minister */}
          <LeaderProfileCard
            id="minister-main"
            engagementKey={ministerItemId}
            level={level}
            tenure="current"
            position="panchayat-minister"
            entityIdOverride={getDignitaryEntityId("minister_panwar")}
            district={enKey.district || district}
            block={enKey.block || block}
            village={enKey.village || village}
            primarySrc="/images/klp.jpg"
            fallbackSrc="/images/pm.jpg"
            alt={t("caption_minister", lang)}
            altKey="caption_minister"
            name={t("pm_name", lang)}
            designation={t("pm_designation", lang)}
          />

          {showPrevBodyUnavailable ? (
            <div className="w-full md:col-span-2">
              <div className="w-full max-w-xl mx-auto rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-6 text-center">
                <span className="inline-flex items-center justify-center rounded-full bg-destructive/15 px-4 py-2 text-destructive font-semibold">
                  {t("data_unavailable", lang)}
                </span>
              </div>
            </div>
          ) : (
            <>
              {/* 3. Local Leader (Sarpanch/Chairperson) */}
              {(() => {
                const data = level === 'gp' ? gpData : level === 'bs' ? bsData : zpData
                const person = level === 'gp' ? (data as V2GP)?.sarpanch : (data as BsData | ZpData)?.chairperson
                const names = level === 'gp' ? (person as V2Names) : (person as BsZpPerson)?.names

                // Resolve Name
                let displayName: string | React.ReactNode = t("data_unavailable", lang);
                let locationSubtext: React.ReactNode = null;

                if (data) {
                  const nameStr = names?.[lang as "en" | "hi" | "pa"] || names?.en;
                  if (nameStr) {
                    displayName = nameStr;
                  } else {
                    displayName = <span className="text-destructive italic">{t("data_unavailable", lang)}</span>;
                  }

                  if (level === "gp") {
                    const locText = villageName || village
                    locationSubtext = <span className="font-normal text-body text-muted-foreground text-center leading-tight px-0 w-full block whitespace-normal break-words">{locText}</span>;
                  }

                } else if (nameError) {
                  displayName = <span className="text-destructive">{nameError}</span>;
                } else {
                  displayName = <span className="text-muted-foreground">Loading...</span>;
                }
                const leaderEngagementDisabled = !data || !(names?.[lang as "en" | "hi" | "pa"] || names?.en)

                const designation =
                  level === "gp"
                    ? `${t("sarpanch_title", lang)}, ${t("level_gp", lang)}`
                    : level === "zp"
                      ? formatTemplate(t("chairperson_zp_line", lang), { parishad: districtName || district })
                      : formatTemplate(t("chairperson_bs_line", lang), { samiti: blockName || block })

                // Unique ID based on location
                console.log("DISTRICT:", district)
                console.log("BLOCK:", block)
                console.log("VILLAGE:", village)
                const locId = `${level}-${district.toLowerCase()}-${block.toLowerCase()}-${village.toLowerCase()}`.replace(/[^a-z0-9-]/g, '');
                const localLeaderItem = {
                  id: `leader-${locId}`,
                  level,
                  district,
                  block,
                  village,
                  role: level === "gp" ? "sarpanch" : "chairperson",
                  names,
                  recordType
                }
                console.log("FULL ITEM:", localLeaderItem)
                const localLeaderItemId = String(extractItemId(localLeaderItem) || generateId(localLeaderItem))
                console.log("FINAL ITEM ID:", localLeaderItemId)

                return (
                  <LeaderProfileCard
                    id={`leader-${locId}`}
                    engagementKey={localLeaderItemId}
                    level={level}
                    tenure={recordType}
                    position={level === "gp" ? "sarpanch" : "chairperson"}
                    engagementDisabled={leaderEngagementDisabled}
                    district={enKey.district || district}
                    block={enKey.block || block}
                    village={enKey.village || village}
                    name={displayName}
                    designation={designation}
                    alt={level === 'gp' ? t("sarpanch_title", lang) : t("chairperson_title", lang)}
                    customImageRenderer={() => (
                      <div className="relative w-full aspect-square max-w-[200px] rounded-xl border-[3px] border-black p-[5px] bg-white shadow-lg overflow-hidden transition-transform hover:scale-105 duration-300 group">
                        <div className="relative w-full h-full rounded-lg bg-[#87CEEB] overflow-hidden">
                          <DynamicImage
                            primarySrc={(() => {
                              const dPath = (enKey.district || district).toLowerCase().replace(/[^a-z0-9]/g, '')
                              const bPath = (enKey.block || block).toLowerCase().replace(/[^a-z0-9]/g, '')
                              if (level === 'zp') return `/images/zp/${dPath}/chairperson.jpg`
                              if (level === 'bs') return `/images/bs/${dPath}/${bPath}/chairperson.jpg`
                              const gpFolder = ((recordType === "previous" ? (gpData?.names?.en || enKey.village || village) : (enKey.village || village)) || "").toLowerCase().replace(/[^a-z0-9]/g, '')
                              return `/images/gp/${dPath}/${bPath}/${gpFolder}/sarpanch.jpg`
                            })()}
                            fallbackSrc="/images/placeholder.svg"
                            alt={level === 'gp' ? t("sarpanch_title", lang) : t("chairperson_title", lang)}
                            width={200}
                            height={200}
                            className="w-full h-full object-cover"
                          />

                          {/* Clickable overlay for WhatsApp image submission */}
                          <button
                            type="button"
                            onClick={handleWhatsAppClick}
                            className="absolute inset-0 flex flex-col items-center justify-center text-center px-2 sarpanch-whatsapp-link sarpanch-box z-10 w-full h-full cursor-pointer"
                            aria-label={`Click to submit your ${getImageCaption(level)} image via WhatsApp`}
                          >
                            <span className="block leading-none mb-1 font-semibold">{t("image_prompt_l1", lang)}</span>
                            <span className="block leading-none mb-1 font-semibold">{t("image_prompt_l2", lang)}</span>
                            <span className="block leading-none font-semibold">{t("image_prompt_l3", lang)}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  >
                    {locationSubtext}
                  </LeaderProfileCard>
                );
              })()}

              {/* 4. Vice-Chairperson (BS/ZP only) */}
              {level !== "gp" && (() => {
                const data = level === "bs" ? bsData : zpData
                const v = (data as (BsData | ZpData) & { viceChairperson?: { names?: V2Names } })?.viceChairperson?.names
                const nameStr = v?.[lang as "en" | "hi" | "pa"] || v?.en || ""
                const displayName = nameStr
                  ? nameStr
                  : <span className="text-destructive italic">{t("data_unavailable", lang)}</span>
                const viceEngagementDisabled = !nameStr

                const designation =
                  level === "zp"
                    ? formatTemplate(t("vice_chairperson_zp_line", lang), { parishad: districtName || district })
                    : formatTemplate(t("vice_chairperson_bs_line", lang), { samiti: blockName || block })

                const dPath = district.toLowerCase().replace(/[^a-z0-9]/g, '')
                const bPath = block.toLowerCase().replace(/[^a-z0-9]/g, '')
                const locId = `vice-${level}-${district}-${block}`.replace(/[^a-z0-9-]/g, '')
                const viceItem = {
                  id: `leader-${locId}`,
                  level,
                  district,
                  block,
                  role: "viceChairperson",
                  names: v,
                  recordType
                }
                console.log("FULL ITEM:", viceItem)
                const viceItemId = String(extractItemId(viceItem) || generateId(viceItem))
                console.log("FINAL ITEM ID:", viceItemId)

                return (
                  <LeaderProfileCard
                    id={`leader-${locId}`}
                    engagementKey={viceItemId}
                    level={level}
                    tenure={recordType}
                    position="vice-chairperson"
                    engagementDisabled={viceEngagementDisabled}
                    district={enKey.district || district}
                    block={enKey.block || block}
                    village={enKey.village || village}
                    name={displayName}
                    designation={designation}
                    alt={getImageCaptionRole(level, "vice")}
                    customImageRenderer={() => (
                      <div className="relative w-full aspect-square max-w-[200px] rounded-xl border-[3px] border-black p-[5px] bg-white shadow-lg overflow-hidden transition-transform hover:scale-105 duration-300 group">
                        <div className="relative w-full h-full rounded-lg bg-[#87CEEB] overflow-hidden">
                          <DynamicImage
                            primarySrc={level === "zp" ? `/images/zp/${dPath}/vice-chairperson.jpg` : `/images/bs/${dPath}/${bPath}/vice-chairperson.jpg`}
                            fallbackSrc="/images/placeholder.svg"
                            alt={getImageCaptionRole(level, "vice")}
                            width={200}
                            height={200}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={handleWhatsAppClickWithName}
                            data-wa-role="vice"
                            data-wa-name={nameStr}
                            className="absolute inset-0 flex flex-col items-center justify-center text-center px-2 sarpanch-whatsapp-link sarpanch-box z-10 w-full h-full cursor-pointer"
                            aria-label={`Click to submit your ${getImageCaptionRole(level, "vice")} image via WhatsApp`}
                          >
                            <span className="block leading-none mb-1 font-semibold">{t("image_prompt_l1", lang)}</span>
                            <span className="block leading-none mb-1 font-semibold">{t("image_prompt_l2", lang)}</span>
                            <span className="block leading-none font-semibold">{t("image_prompt_l3", lang)}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  >
                  </LeaderProfileCard>
                )
              })()}
            </>
          )}
        </div>

        {(level === "gp" ? (district && block && village) : level === "bs" ? (district && block) : district) && (
          <div className="w-full flex items-center justify-center gap-4 mt-6">
            <button
              type="button"
              onClick={() => setRecordType("previous")}
              className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${recordType === "previous" ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted border-input"
                }`}
              disabled={switchLoading}
              aria-pressed={recordType === "previous"}
            >
              {getPreviousBodyLabel(level)}
            </button>
            <button
              type="button"
              onClick={() => setRecordType("current")}
              className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${recordType === "current" ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted border-input"
                }`}
              disabled={switchLoading}
              aria-pressed={recordType === "current"}
            >
              {formatTemplate(t("curr_body_label", lang), {
                level: level === "gp" ? t("level_gp", lang) : level === "bs" ? t("level_bs", lang) : t("level_zp", lang)
              })}
            </button>
          </div>
        )}

        {switchError && (
          <p className="text-center text-destructive mt-3">{switchError}</p>
        )}
        {switchLoading && (
          <p className="text-center mt-3">{t("loading", lang) || "Loading..."}</p>
        )}

        {/* GP/BS/ZP Details Section: Wards List (moved below leaders) */}
        {showPrevBodyUnavailable && (
          <section className="mt-12 max-w-6xl mx-auto px-4">
            <div className="w-full rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-10 text-center">
              <span className="inline-flex items-center justify-center rounded-full bg-destructive/15 px-5 py-2.5 text-destructive font-semibold text-base">
                {t("data_unavailable", lang)}
              </span>
            </div>
          </section>
        )}
        {((level === "gp" && gpData) || (level === "bs" && bsData) || (level === "zp" && zpData)) && !switchLoading && (
          <section className="mt-12 max-w-6xl mx-auto px-4">
            <WardList
              wards={(level === "gp" ? gpData?.wards : level === "bs" ? bsData?.wards : zpData?.wards)?.map((w) => ({
                number: w.number,
                en: w.names?.en || "",
                hi: w.names?.hi || "",
                pa: w.names?.pa || "",
              }))}
              lang={lang}
              district={district}
              block={block}
              village={level === "gp"
                ? (recordType === "previous"
                  ? (gpData?.names?.en || gpVillageLookup)
                  : gpVillageLookup)
                : village}
              pathDistrict={level === "gp" ? (enKey.district || district) : district}
              pathBlock={level === "gp" ? (enKey.block || block) : block}
              pathVillage={level === "gp"
                ? (recordType === "previous"
                  ? (gpData?.names?.en || enKey.village || village)
                  : (enKey.village || village))
                : village}
              districtName={districtName}
              blockName={blockName}
              villageName={level === "gp" ? (gpData?.names?.[lang as "en" | "hi" | "pa"] || villageName) : villageName}
              level={level}
              isFormerTenure={recordType === "previous"}
            />
          </section>
        )}
        {((level === "gp" && !gpData) || (level === "bs" && !bsData) || (level === "zp" && !zpData)) && !switchLoading && nameError && (
          <p className="text-center text-muted-foreground mt-6">{nameError}</p>
        )}
      </section>
    </main>
  )
}

export default GramPanchayatPage
